import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import React from 'react';
import './index.scss';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Defaults to the placeholder, which is what every caller was passing anyway. */
  ariaLabel?: string;
  /** The input's id, so a FieldLabel above it can point at it (htmlFor) in a labelled filter row. */
  id?: string;
  /**
   * Shows a clear button while there is something to clear. Omit where clearing needs no side
   * effect beyond emptying the box, since the user can select-all and delete.
   */
  onClear?: () => void;
  /** The magnifier. Off for the per-column filter rows, where one per row would be noise. */
  icon?: boolean;
  onKeyDown?: React.KeyboardEventHandler;
  onBlur?: React.FocusEventHandler;
  autoFocus?: boolean;
  /** The keyboard a phone shows. "numeric" for a number bound such as years or days. */
  inputMode?: 'text' | 'numeric' | 'decimal';
}

/**
 * The console's search box.
 *
 * <p>A search box legitimately differs from a form field: it sits on `--ink-750` rather than the
 * form fill, and it carries a magnifier. Its height does not differ: it is --control-h like every
 * other control (it was 36px, a tier of its own, until 2026-10-07). What it should not
 * do is differ from itself. That spec lived in `SearchFilter.scss` and applied only to the two
 * inputs inside that component, so the record picker's search box, which is the same control doing
 * the same job, was an untouched `TextField` a few pixels taller on a different background.</p>
 *
 * <p>This is not `WorkspaceInput` and not `FormField`. Those two are for entering a value that gets
 * saved; this one filters what is already on screen and is thrown away, which is why it owns a
 * clear affordance and they do not.</p>
 */
const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
  id,
  onClear,
  icon = true,
  onKeyDown,
  onBlur,
  autoFocus = false,
  inputMode,
}) => (
  <TextField
    className="cc-search-input"
    fullWidth
    size="small"
    variant="outlined"
    autoFocus={autoFocus}
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={onKeyDown}
    onBlur={onBlur}
    slotProps={{
      htmlInput: { id, 'aria-label': ariaLabel ?? placeholder, inputMode },
      input: {
        startAdornment: icon ? (
          <InputAdornment position="start">
            <SearchIcon className="cc-search-input__icon" />
          </InputAdornment>
        ) : undefined,
        endAdornment:
          onClear && value ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="Clear search"
                size="small"
                className="cc-search-input__clear"
                onClick={onClear}
              >
                <CloseIcon className="cc-search-input__clear-icon" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
      },
    }}
  />
);

export default SearchInput;
